import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArticleTemplate,
  articleTemplatesApi,
} from '@texas/api/endpoints/articleTemplatesApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { ArticleTemplateModal } from './ArticleTemplateModal';

export function ArticleTemplates() {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const canEdit = hasRole(currentUser.role, [Role.SystemAdministrator]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<ArticleTemplate | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, loading, error } = useApiResource(
    articleTemplatesApi.getAll,
  );

  useEffect(() => {
    refetch(currentUser.organizationId);
  }, [currentUser.organizationId, refetch]);

  const templates = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((template) => !template.archived)
      .filter((template) => template.name.toLowerCase().includes(normalized))
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }, [data, filter]);

  return (
    <ConfigurationPage
      title={t('configuration.navigation.articleTemplates')}
      toolbar={
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          onChange={setFilter}
        />
      }
      action={
        canEdit ? (
          <Button
            variant="entityDirectoryPrimary"
            onClick={() => {
              setSelected(null);
              onOpen();
            }}
          >
            {t('configuration.newArticleTemplate')}
          </Button>
        ) : undefined
      }
    >
      <ArticleTemplateModal
        canEdit={canEdit}
        isOpen={isOpen}
        onClose={onClose}
        onSaved={() => refetch(currentUser.organizationId)}
        template={selected}
      />
      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
              <Th>{t('branch.displayOrder')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && templates.length === 0 && (
              <Tr>
                <Td colSpan={2}>{t('general.noData')}</Td>
              </Tr>
            )}
            {templates.map((template) => (
              <Tr
                key={template.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelected(template);
                  onOpen();
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelected(template);
                  onOpen();
                }}
              >
                <Td>{template.name}</Td>
                <Td>{template.displayOrder}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
